import React, { useState } from "react";
import { Message, useToaster } from "rsuite";
import axiosInstance from "../../../../../../appHelpers/axiosInstance";
import { useAuth0 } from "@auth0/auth0-react";
import Modal from "../../../../../ui/Modal/Modal";
import Button from "../../../../../ui/Button/Button";
import Input from "../../../../../ui/Input/Input";
import styled from "styled-components";
import { colors } from "../../../../../ui/theme/colors";

export const CreateSiteGroup = ({ customer, update, setUpdate }) => {
  const { getAccessTokenSilently } = useAuth0();
  const toaster = useToaster();
  const initFormGroup = {
    name: "",
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setFormValue(initFormGroup);
  };

  const [open, setOpen] = useState(false);
  const [formValue, setFormValue] = useState(initFormGroup);
  const [loading, setLoading] = useState(false);

  const successMessage = (
    <Message showIcon type="success" header="Success">
      Site group "{formValue.name}" was created successfully.
    </Message>
  );

  const errorMessage = (
    <Message showIcon type="error" header="Error">
      Failed to create site group. Please try again.
    </Message>
  );

  const onSubmit = async () => {
    if (!formValue.name || formValue.name.trim() === "") {
      toaster.push(
        <Message showIcon type="warning" header="Warning">
          Please enter a group name.
        </Message>,
        "topCenter",
      );
      return;
    }

    setLoading(true);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "POST",
        url: `/customer/site-groups`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          customer_id: customer,
          name: formValue.name.trim(),
        }),
      });

      setUpdate(!update);
      toaster.push(successMessage, "topCenter");
      handleClose();
    } catch (error) {
      console.error("Error creating site group:", error);
      toaster.push(errorMessage, "topCenter");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button onClick={handleOpen} size="medium">
        Create New Group
      </Button>

      <Modal
        open={open}
        onClose={handleClose}
        title="Create New Site Group"
        size="sm"
        showFooter={true}
        footerActions={
          <StyledFooterActions>
            <Button variant="subtle" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="primary" onClick={onSubmit} loading={loading}>
              Create Group
            </Button>
          </StyledFooterActions>
        }
      >
        <ModalContent>
          <FormGroup>
            <Label>Group Name *</Label>
            <Input
              value={formValue.name}
              onChange={(value) => setFormValue({ ...formValue, name: value })}
              placeholder="Enter group name..."
              autoFocus
            />
            <HelpText>Enter a descriptive name for this site group.</HelpText>
          </FormGroup>
        </ModalContent>
      </Modal>
    </>
  );
};

const ModalContent = styled.div`
  padding: 24px;
`;

const StyledFooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.neutral[700]};
`;

const HelpText = styled.div`
  font-size: 12px;
  color: ${colors.neutral[500]};
  line-height: 1.4;
  margin-top: 4px;
`;
